import React, { useState, useEffect } from 'react';
import {
  Box,
  Container as MuiContainer,
  Typography,
  Breadcrumbs,
  Link,
  Paper,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Business as OperationsIcon,
  LocationOn as LocateIcon,
} from '@mui/icons-material';
import { useNavigationCore } from '../../../../core/router/navigation';
import { useMeCore } from '../../../../core/modules/me';
import { useCompanyCore } from '../../../../core/modules/company';
import { useContainerCore } from '../../../../core/modules/container';
import {
  LocateContainerList,
  LocateContainerDialog,
} from '../../../components/operations/locateContainer';
import { Container } from '../../../../core/interfaces/container.interface';

export const LocateContainerScreen: React.FC = () => {
  const { goToWelcome } = useNavigationCore();
  const { user } = useMeCore();
  const { companyId } = useCompanyCore();
  const { options, setOptions, getContainers } = useContainerCore();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedContainerIds, setSelectedContainerIds] = useState<number[]>([]);
  const [allContainers, setAllContainers] = useState<Container[]>([]);
  const [loadedContainerIds, setLoadedContainerIds] = useState<Set<number>>(new Set());

  // Verificar permisos
  useEffect(() => {
    if (
      !user ||
      (!user.privileges.superAdmin &&
        !user.privileges.readContainer &&
        !user.privileges.writeContainer)
    ) {
      goToWelcome();
    }
  }, [user, goToWelcome]);

  // Cargar contenedores inicialmente
  useEffect(() => {
    if (companyId) {
      getContainers(companyId, options);
    }
  }, [companyId, getContainers, options]);

  // Limpiar selección cuando cambian filtros significativos (no página)
  useEffect(() => {
    const { page, ...filtersWithoutPage } = options;
    const hasFilters = Object.keys(filtersWithoutPage).length > 0;
    
    if (hasFilters) {
      // No limpiar selección, solo acumular contenedores
    }
  }, [options]);

  const handleContainerSelect = (containerId: number) => {
    setSelectedContainerIds(prev => 
      prev.includes(containerId) 
        ? prev.filter(id => id !== containerId)
        : [...prev, containerId]
    );
  };

  const handleSelectAll = (containerIds: number[]) => {
    const allSelected = containerIds.every(id => selectedContainerIds.includes(id));
    if (allSelected) {
      // Deseleccionar todos los de la página actual
      setSelectedContainerIds(prev => prev.filter(id => !containerIds.includes(id)));
    } else {
      // Seleccionar todos los de la página actual
      setSelectedContainerIds(prev => [...new Set([...prev, ...containerIds])]);
    }
  };

  const handleOpenDialog = () => {
    const selectedContainers = allContainers.filter(c => selectedContainerIds.includes(c.id));
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleSuccess = () => {
    // Refrescar la lista de contenedores
    if (companyId) {
      getContainers(companyId, options);
    }
    setSelectedContainerIds([]);
    setAllContainers([]);
  };

  const handleClearSelection = () => {
    setSelectedContainerIds([]);
  };

  const handleRemoveFromSelection = (containerId: number) => {
    setSelectedContainerIds(prev => prev.filter(id => id !== containerId));
  };

  const handleContainersLoaded = (newContainers: Container[]) => {
    setAllContainers(prev => {
      const existingIds = new Set(prev.map(c => c.id));
      const newContainersToAdd = newContainers.filter(c => !existingIds.has(c.id));
      return [...prev, ...newContainersToAdd];
    });
  };

  // Obtener contenedores seleccionados para el modal
  const selectedContainers = allContainers.filter(c => selectedContainerIds.includes(c.id));


  return (
    <MuiContainer maxWidth="xl" sx={{ py: 3 }}>

      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <LocateIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h4" component="h1">
            Ubicar Contenedores
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Gestiona la ubicación de contenedores. Selecciona los contenedores que
          deseas ubicar y asígnalos a su nueva ubicación.
        </Typography>
      </Paper>


      {/* Lista de contenedores con filtros integrados */}
      <LocateContainerList 
        onOpenLocateDialog={handleOpenDialog}
        selectedContainerIds={selectedContainerIds}
        onContainerSelect={handleContainerSelect}
        onSelectAll={handleSelectAll}
        onContainersLoaded={handleContainersLoaded}
        onClearSelection={handleClearSelection}
      />

      {/* Diálogo de ubicación */}
      <LocateContainerDialog
        open={dialogOpen}
        containers={selectedContainers}
        onClose={handleCloseDialog}
        onSuccess={handleSuccess}
        onRemoveContainer={handleRemoveFromSelection}
      />
    </MuiContainer>
  );
};

export default LocateContainerScreen;
